import { applyHeroWeightSlideFallback } from './heroGeoRestriction';

// Hero Carousel Plugin
(function () {
    // Timing configuration constants
    const AUTO_ADVANCE_INTERVAL = 5000;
    const VIDEO_FADE_DURATION = 0.6; // Seconds for video fade-in CSS transition

    // Detect if user is on Apple device (macOS or iOS)
    function isAppleDevice() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const platform = navigator.platform || '';

        // Check for iOS devices via userAgent
        if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
            return true;
        }

        // Check for macOS via userAgent (works even when platform is deprecated)
        if (/Macintosh|MacIntel|MacPPC|Mac68K|Mac OS X/.test(userAgent)) {
            return true;
        }

        // Fallback: Check for macOS via platform (may be deprecated in some browsers)
        if (platform.toUpperCase().indexOf('MAC') >= 0) {
            return true;
        }

        // Additional check for iPad on iOS 13+ (which reports as MacIntel)
        if (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(platform)) {
            return true;
        }

        return false;
    }

    // Get the appropriate color based on OS
    function getBackgroundColor(backgroundContent) {
        if (!backgroundContent) return null;

        // If on Apple device and 'color-apple' exists, use it
        if (isAppleDevice() && backgroundContent['color-apple']) {
            return backgroundContent['color-apple'];
        }

        // Otherwise use the default color
        return backgroundContent['color'] || null;
    }

    function initHeroCarousel() {
        const heroCarousel = document.querySelector('.hero-home .carousel');

        if (!heroCarousel) {
            return; // Exit if hero carousel doesn't exist
        }

        const heroItemsData = JSON.parse(heroCarousel.getAttribute('data-hero-items') || '[]');

        if (heroItemsData.length === 0) {
            return; // Exit if no items
        }

        let currentIndex = 0;

        heroCarousel.style.setProperty('--hero-interval', `${AUTO_ADVANCE_INTERVAL}ms`);

        const carouselItems = heroCarousel.querySelectorAll('.carousel-item');
        const leftSideContent = heroCarousel.querySelector('.left-side-content');
        const navigationItems = heroCarousel.querySelectorAll('.hero-navigation-item');
        const heroTitle = leftSideContent ? leftSideContent.querySelector('[data-hero-title]') : null;
        const heroDescription = leftSideContent ? leftSideContent.querySelector('[data-hero-description]') : null;
        const heroLink = leftSideContent ? leftSideContent.querySelector('[data-hero-link]') : null;
        const heroLinkText = leftSideContent ? leftSideContent.querySelector('[data-hero-link-text]') : null;

        // Initialize first item
        function initializeFirstItem() {
            if (carouselItems.length === 0) return;

            const firstItem = carouselItems[0];
            const firstItemData = heroItemsData[0];

            // Set background color if provided (OS-specific)
            if (firstItemData && firstItemData['background-content']) {
                const bgColor = getBackgroundColor(firstItemData['background-content']);
                if (bgColor) {
                    heroCarousel.closest('.hero-home').style.backgroundColor = bgColor;
                    // Also update the carousel item's inline background
                    firstItem.style.backgroundColor = bgColor;
                }
            }

            const firstVideo = firstItem.querySelector('.carousel-item-video');
            if (firstVideo && !firstVideo.hasAttribute('data-lazy')) {
                firstVideo.style.opacity = '0';
                firstVideo.style.transition = `opacity ${VIDEO_FADE_DURATION}s ease`;

                const fadeInVideo = () => {
                    firstVideo.style.opacity = '1';
                    firstVideo.removeEventListener('loadeddata', fadeInVideo);
                    firstVideo.removeEventListener('canplay', fadeInVideo);
                };

                firstVideo.addEventListener('loadeddata', fadeInVideo);
                firstVideo.addEventListener('canplay', fadeInVideo);

                if (firstVideo.readyState >= 3) {
                    fadeInVideo();
                }

                firstVideo.play().catch(e => console.log('Video autoplay prevented:', e));
            }

            if (firstVideo && firstVideo.hasAttribute('data-lazy')) {
                const loadAndPlayFirstVideo = () => {
                    if (!firstVideo.hasAttribute('data-lazy')) return;
                    loadVideoSource(firstItem);
                    playVideoWithFadeIn(firstVideo);
                };

                if ('requestIdleCallback' in window) {
                    window.requestIdleCallback(loadAndPlayFirstVideo, { timeout: 1200 });
                } else {
                    setTimeout(loadAndPlayFirstVideo, 800);
                }
            }
        }

        /** Single <source> from data-* URLs so mobile-first loads swap correctly (native <source media> + load() is unreliable). */
        function applyHeroVideoSourcesToViewport(video) {
            const urlMobile = (video.getAttribute('data-src-mobile') || '').trim();
            const urlDesktop = (video.getAttribute('data-src-desktop') || '').trim();
            const useMobile = window.matchMedia('(max-width: 768px)').matches && urlMobile.length > 0;
            const url = useMobile ? urlMobile : urlDesktop;
            if (!url) {
                return false;
            }
            while (video.firstElementChild) {
                video.removeChild(video.firstElementChild);
            }
            video.pause();
            video.removeAttribute('src');
            video.src = url;
            return true;
        }

        function reinsertVideoForIos(video) {
            const parent = video.parentNode;
            const nextSibling = video.nextSibling;
            parent.removeChild(video);
            parent.insertBefore(video, nextSibling);
        }

        // Load video lazily (just loads the source, doesn't play)
        function loadVideoSource(item) {
            const video = item.querySelector('.carousel-item-video[data-lazy]');
            if (!video) return false;

            const urlDesktop = (video.getAttribute('data-src-desktop') || '').trim();
            if (!urlDesktop) return false;

            video.removeAttribute('data-lazy');

            if (!applyHeroVideoSourcesToViewport(video)) {
                return false;
            }

            reinsertVideoForIos(video);
            video.load();
            return true;
        }

        // Play video with fade-in effect
        function playVideoWithFadeIn(video) {
            if (!video) return;

            // Set initial opacity to 0 for fade-in effect
            video.style.opacity = '0';
            video.style.transition = `opacity ${VIDEO_FADE_DURATION}s ease`;

            // Fade in video when it's ready to play
            const fadeInVideo = () => {
                video.style.opacity = '1';
            };

            // Do not seek while readyState is HAVE_NOTHING — breaks load() right after assigning src.
            if (video.readyState >= 1) {
                try {
                    video.currentTime = 0;
                } catch (e) {
                    /* ignore */
                }
            }

            // If video has ended, reload it to restart properly
            if (video.ended) {
                video.load();
            }

            // Handle fade-in based on video state
            if (video.readyState >= 3 && !video.ended) {
                // Video is already loaded and ready, fade in immediately
                // This handles the case when cycling back to the first video
                setTimeout(() => {
                    fadeInVideo();
                }, 50);
            } else {
                // Wait for video to be ready
                const handleReady = () => {
                    fadeInVideo();
                    video.removeEventListener('loadeddata', handleReady);
                    video.removeEventListener('canplay', handleReady);
                    video.removeEventListener('seeked', handleReady);
                };

                video.addEventListener('loadeddata', handleReady);
                video.addEventListener('canplay', handleReady);
                // Also listen for seeked in case we're seeking to 0
                video.addEventListener('seeked', handleReady);
            }

            // Play video
            video.play().catch(e => console.log('Video autoplay prevented:', e));
        }

        // Update left side content
        function updateLeftSideContent(index) {
            if (!leftSideContent || !heroItemsData[index]) return;

            const itemData = heroItemsData[index];
            const leftContent = itemData['left-side-content'];

            if (!leftContent) return;

            // Update title
            if (heroTitle && leftContent.title) {
                heroTitle.textContent = leftContent.title;
            }

            // Update description
            if (heroDescription && leftContent.description) {
                heroDescription.textContent = leftContent.description;
            }

            // Update link
            if (heroLink && leftContent.link) {
                heroLink.setAttribute('href', leftContent.link.url);

                // Update link text
                if (heroLinkText) {
                    heroLinkText.textContent = leftContent.link.text || 'Explore now';
                }

                // // Update icon if exists
                // const existingIcon = heroLink.querySelector('.hero-link-icon');
                // if (leftContent.link.icon) {
                //     if (existingIcon) {
                //         existingIcon.setAttribute('src', leftContent.link.icon);
                //     } else {
                //         const icon = document.createElement('img');
                //         icon.src = leftContent.link.icon;
                //         icon.alt = '';
                //         icon.className = 'hero-link-icon';
                //         heroLink.insertBefore(icon, heroLinkText);
                //     }
                // } else if (existingIcon) {
                //     existingIcon.remove();
                // }
            }
        }

        // Update navigation active state
        function updateNavigation(index) {
            // Temporarily disable transitions for instant feedback
            navigationItems.forEach((navItem) => {
                const link = navItem.querySelector('.hero-navigation-item-link');
                if (link) {
                    link.style.transition = 'none';
                }
            });

            navigationItems.forEach((navItem, i) => {
                if (i === index) {
                    navItem.classList.add('active');
                } else {
                    navItem.classList.remove('active');
                }
            });

            // Force immediate reflow to apply styles instantly
            if (navigationItems[index]) {
                void navigationItems[index].offsetHeight;
            }

            // Re-enable transitions after a brief moment for smooth animations on other properties
            requestAnimationFrame(() => {
                navigationItems.forEach((navItem) => {
                    const link = navItem.querySelector('.hero-navigation-item-link');
                    if (link) {
                        link.style.transition = '';
                    }
                });
            });
        }

        function switchToItem(index) {
            if (index === currentIndex || index < 0 || index >= heroItemsData.length) {
                return;
            }

            currentIndex = index;
            updateNavigation(index);
            updateLeftSideContent(index);
        }

        function startAutoAdvance() {
            if (heroItemsData.length <= 1) return;
            setInterval(() => {
                switchToItem((currentIndex + 1) % heroItemsData.length);
            }, AUTO_ADVANCE_INTERVAL);
        }

        // Navigation items are now actual links - no need to prevent default or switch items
        // navigationItems.forEach((navItem, index) => {
        //     const link = navItem.querySelector('.hero-navigation-item-link');
        //     if (link) {
        //         link.addEventListener('click', (e) => {
        //             e.preventDefault();
        //             if (index !== currentIndex && !isTransitioning) {
        //                 switchToItem(index);
        //             }
        //         });
        //     }
        // });

        function isHeroVideoMobileViewport() {
            return window.matchMedia('(max-width: 768px)').matches;
        }

        function debounce(fn, ms) {
            let t;
            return function debounced(...args) {
                clearTimeout(t);
                t = setTimeout(() => fn.apply(this, args), ms);
            };
        }

        let lastHeroVideoMobileLayout = isHeroVideoMobileViewport();

        const reloadActiveHeroVideoForSourceMatch = () => {
            const item = heroCarousel.querySelector('.carousel-item.active');
            const video = item ? item.querySelector('.carousel-item-video') : null;
            if (!video || video.hasAttribute('data-lazy')) {
                return;
            }
            if (!applyHeroVideoSourcesToViewport(video)) {
                return;
            }
            reinsertVideoForIos(video);
            video.load();
            video.style.opacity = '1';
            video.play().catch(() => {});
        };

        const runHeroVideoLayoutTierChange = () => {
            const nowMobile = isHeroVideoMobileViewport();
            if (nowMobile === lastHeroVideoMobileLayout) {
                return;
            }
            lastHeroVideoMobileLayout = nowMobile;
            reloadActiveHeroVideoForSourceMatch();
        };

        const onHeroViewportResize = debounce(runHeroVideoLayoutTierChange, 200);

        window.addEventListener('resize', onHeroViewportResize, { passive: true });

        const mqHero768 = window.matchMedia('(max-width: 768px)');
        if (typeof mqHero768.addEventListener === 'function') {
            mqHero768.addEventListener('change', runHeroVideoLayoutTierChange);
        } else if (typeof mqHero768.addListener === 'function') {
            mqHero768.addListener(runHeroVideoLayoutTierChange);
        }

        // Initialize
        initializeFirstItem();
        startAutoAdvance();
        applyHeroWeightSlideFallback();
    }

    // Run as soon as this script executes if the hero markup is already parsed (script is after the carousel).
    // Waiting for DOMContentLoaded delays until the entire document is parsed (rest of page after hero).
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initHeroCarousel);
    } else {
        initHeroCarousel();
    }
})();



// WEBPACK FOOTER //
// ./resources/assets/js/heroCarousel.plugin.js